import { useEffect, useState } from 'react';
import './landingpage.css';

const projects = [
  {
    slug: 'shifttrack',
    title: 'ShiftTrack',
    description:
      'A full-stack application for organizing shift responsibilities, assigning tasks, and tracking completed, pending, and overdue work. Built with Next.js, React, TypeScript, Tailwind CSS, and Supabase.',
    meta: 'Next.js · TypeScript · Supabase · PostgreSQL',
    challenge:
      'Give teams a shared view of shift responsibilities, with clear task ownership and progress tracking while keeping each workspace’s records separate.',
    approach: [
      'Built a dashboard with search and filters by task status and teammate to make daily responsibilities easier to find and track.',
      'Implemented Owner, Manager, and Employee roles with database-enforced workspace separation using Supabase and PostgreSQL.',
      'Developed daily, weekly, and monthly tasks that create their next occurrence when completed, with time zone handling and duplicate prevention.',
      'Built responsive layouts, account registration, email confirmation, password recovery, and workspace onboarding.',
      'Added automated browser and database tests covering task workflows, duplicate occurrence prevention, and time zone considerations.',
    ],
    outcomes: [
      'Built a full-stack project that connects a responsive interface to authentication, permissions, and persistent task data.',
      'Applied operational workflow concepts to task assignment, recurring responsibilities, and status tracking.',
      'Practiced testing application behavior and database boundaries together to support reliable team workflows.',
    ],
    proof: [
      {
        title: 'ShiftTrack Repository',
        type: 'Source code',
        description:
          'Explore the application source, feature overview, setup instructions, and implementation notes in the project repository.',
        href: 'https://github.com/rumbxo/ShiftTrack',
        linkLabel: 'View on GitHub',
      },
      {
        title: 'Database Design and Permissions',
        type: 'Architecture evidence',
        description:
          'Documentation of workspace isolation, Owner/Manager/Employee permissions, row-level security, and task recurrence transactions.',
        href: 'https://github.com/rumbxo/ShiftTrack/blob/main/supabase/README.md',
        linkLabel: 'Review database design',
      },
      {
        title: 'Task Workflow Browser Tests',
        type: 'Test evidence',
        description:
          'Playwright tests covering shared task assignment, employee permissions, completion, and recurring tasks using an isolated local test service.',
        href: 'https://github.com/rumbxo/ShiftTrack/blob/main/tests/tasks.spec.ts',
        linkLabel: 'Review browser tests',
      },
    ],
  },
  {
    slug: 'history-of-video-games',
    title: 'History of Video Games Website',
    description:
      'A responsive multi-page website built for a Multimedia Web Design class. The site explores video game history through a timeline, arcade era, console history, PC gaming, modern gaming, an embedded video, gallery content, and a contact form.',
    meta: 'HTML5 · CSS3 · Multimedia Web Design · Responsive Design',
    challenge:
      'Create an engaging educational website that presents the history of video games clearly while meeting class requirements for multi-page structure, media, navigation, responsive design, and validation.',
    approach: [
      'Planned a seven-page site structure covering the home page, timeline, arcade era, consoles, PC gaming, modern gaming, and contact page.',
      'Built responsive HTML and CSS layouts with a consistent navigation system, styled content cards, a timeline table, gallery elements, and form controls.',
      'Added multimedia and quality checks, including an embedded video, page metadata, W3C HTML/CSS validation, and accessibility review.',
    ],
    outcomes: [
      'Delivered a complete class website that visitors can browse as a real working artifact.',
      'Practiced semantic page structure, responsive styling, embedded media, tables, forms, and multi-page navigation.',
      'Confirmed the final site passed HTML validation, CSS validation, and accessibility review with no major issues noted.',
    ],
    proof: [
      {
        title: 'Completed Class Website',
        type: 'Project artifact',
        description:
          'The full History of Video Games website is available as a browsable artifact with all class pages and styling included.',
        href: '/projects/video-game-website/index.html',
        linkLabel: 'Open website',
      },
      {
        title: 'Seven-page Website Scope',
        type: 'Implementation evidence',
        description:
          'The project includes pages for the timeline, arcade era, consoles, PC gaming, modern gaming, and a contact form.',
        href: '/projects/video-game-website/timeline.html',
        linkLabel: 'View timeline',
      },
      {
        title: 'Validation Report',
        type: 'Process evidence',
        description:
          'The final validation notes show the HTML, CSS, and accessibility review results for the submitted website.',
        href: '/projects/video-game-website/VALIDATIONS.txt',
        linkLabel: 'View validation notes',
      },
    ],
  },
  {
    slug: 'healthcare-cybersecurity-system',
    title: 'Healthcare Cybersecurity System',
    description:
      'A systems-analysis project proposing an Endpoint Detection and Response system for a small medical office. The design addresses ransomware, protected health information, access control, incident response, and system implementation.',
    meta: 'Cybersecurity · Systems Analysis · Risk Management',
    challenge:
      'Design a safer endpoint security environment for a healthcare setting where ransomware, protected health information, system visibility, and HIPAA safeguards are major concerns.',
    approach: [
      'Proposed a centralized EDR system to provide real-time monitoring, endpoint threat detection, alerting, and response capabilities.',
      'Analyzed technical, financial, operational, legal, maintenance, and implementation risks tied to healthcare security operations.',
      'Created a phased project plan covering assessment, architecture, vendor selection, implementation, staff training, testing, closure, and monitoring handoff.',
    ],
    outcomes: [
      'Created a practical security proposal for implementing EDR in a small medical office.',
      'Connected endpoint protection controls to ransomware prevention, PHI protection, auditability, and HIPAA-aligned safeguards.',
      'Built experience explaining feasibility, risk mitigation, use cases, cost planning, and implementation sequencing for non-technical stakeholders.',
    ],
    proof: [
      {
        title: 'EDR Implementation Paper',
        type: 'PDF artifact',
        description:
          'A seven-page paper proposing centralized Endpoint Detection and Response for a medical office, including feasibility, risks, project phases, design, use cases, and implementation.',
        href: '/projects/healthcare-cybersecurity-system-paper.pdf',
        linkLabel: 'Open PDF',
      },
      {
        title: 'Original Word Document',
        type: 'Source artifact',
        description:
          'The original DOCX paper is available as the source document for the project artifact.',
        href: '/projects/healthcare-cybersecurity-system-paper.docx',
        linkLabel: 'Download DOCX',
      },
      {
        title: 'Risk and Project Planning',
        type: 'Analysis evidence',
        description:
          'The proposal includes technical, operational, financial, maintenance, and legal risk mitigation plus a phased implementation plan estimated at roughly four months.',
        href: '/projects/healthcare-cybersecurity-system-paper.pdf',
        linkLabel: 'Review in paper',
      },
    ],
  },
  {
    slug: 'interactive-cpp-quiz-game',
    title: 'Interactive C++ Quiz Game',
    description:
      'A multiple-choice quiz application with difficulty levels, player-name capture, answer validation, score tracking, structured question data, and the option to restart the game.',
    meta: 'C++ · Data Structures · Input Validation',
    challenge:
      'Build a console-based quiz game that feels interactive while keeping the code structured, reliable, and easy to follow.',
    approach: [
      'Structured quiz questions and answers so the game could support different difficulty levels.',
      'Added player-name capture, answer validation, score tracking, and restart logic.',
      'Focused on clear control flow and defensive input handling to reduce user-entry errors.',
    ],
    outcomes: [
      'Delivered a complete playable C++ project with multiple user flows.',
      'Practiced data structures, conditionals, loops, functions, and validation.',
      'Improved confidence turning programming concepts into a finished experience.',
    ],
    proof: [
      {
        title: 'C++ Quiz Source Code',
        type: 'PDF artifact',
        description:
          'The project artifact shows the actual C++ source code for the quiz game, including includes, data structures, function declarations, main game loop, and helper functions.',
        href: '/projects/interactive-cpp-quiz-game.pdf',
        linkLabel: 'Open PDF',
      },
      {
        title: 'Question Data Structure',
        type: 'Implementation evidence',
        description:
          'The code defines a Question struct with text, answer options, and a correct answer, then stores quiz data in vectors for easy and hard difficulty modes.',
        href: '/projects/interactive-cpp-quiz-game.pdf',
        linkLabel: 'Review code',
      },
      {
        title: 'Input and Replay Flow',
        type: 'Logic evidence',
        description:
          'The game captures the player name, validates difficulty and answer input, tracks score, calculates percentage results, and lets the player restart.',
        href: '/projects/interactive-cpp-quiz-game.pdf',
        linkLabel: 'View game loop',
      },
    ],
  },
  {
    slug: 'network-security-design',
    title: 'Network Security Diagram',
    description:
      'A CIS244 Network Security assignment using Dia Diagram to model a wired and wireless network, identify security weaknesses, and recommend mitigations for wireless traffic, router access, rogue access points, IoT devices, and firmware risks.',
    meta: 'Network Security · Dia Diagram · Wireless Security · Risk Mitigation',
    challenge:
      'Create a network diagram that shows common wired and wireless components while identifying realistic attack points and explaining how to reduce security risk.',
    approach: [
      'Built the network layout in Dia Diagram with Internet, modem, firewall, wireless router, switch, wired workstations, computers, and wireless devices.',
      'Labeled risk areas in the diagram, including unencrypted traffic, weak Wi-Fi passwords, default router login, no VPN, and firmware vulnerabilities.',
      'Documented mitigations such as WPA3 encryption, strong passphrases, MAC filtering, wireless intrusion detection, HTTPS/SSL, VPN usage, IoT VLAN segmentation, and firmware updates.',
    ],
    outcomes: [
      'Produced a finished network diagram and written assignment connecting network architecture to practical security controls.',
      'Practiced identifying wireless network threats such as eavesdropping, rogue access points, man-in-the-middle attacks, IoT exploits, and default credential risk.',
      'Built experience documenting security recommendations clearly for both technical and non-technical readers.',
    ],
    proof: [
      {
        title: 'Finished Network Diagram',
        type: 'Image artifact',
        description:
          'A completed Dia network diagram showing Internet, modem, firewall, wireless router, switch, wired devices, wireless devices, and labeled vulnerability areas.',
        href: '/projects/network-security-diagram/network-diagram.png',
        linkLabel: 'Open diagram',
      },
      {
        title: 'Network Diagram Assignment',
        type: 'DOCX artifact',
        description:
          'The written CIS244 assignment explains the diagram process, identified threats, mitigations, conclusion, and references.',
        href: '/projects/network-security-diagram/network-diagram-assignment.docx',
        linkLabel: 'Download DOCX',
      },
      {
        title: 'Dia Source File',
        type: 'Source artifact',
        description:
          'The original Dia diagram file is included as the editable source artifact behind the exported network diagram screenshot.',
        href: '/projects/network-security-diagram/network-diagram.dia',
        linkLabel: 'Download DIA',
      },
    ],
  },
  {
    slug: 'cloud-migration-strategy',
    title: 'Cloud Computing Strategy',
    description:
      'A CIS342 Cloud Computing and Virtualization paper evaluating cloud adoption as a strategy for organizational transformation. The project weighs cloud benefits, drawbacks, migration planning, scalability, performance, security, compliance, and cost control.',
    meta: 'Cloud Computing · Virtualization · Migration Planning · Security',
    challenge:
      'Evaluate whether an organization should adopt cloud computing and create a workable migration strategy that balances productivity, agility, security, performance, scalability, and economic control.',
    approach: [
      'Analyzed cloud computing advantages such as productivity, agility, cost control, reliability, security tooling, and support for mission-critical operations.',
      'Reviewed adoption drawbacks and planning concerns, including migration complexity, compliance, performance, and the need for disciplined cost management.',
      'Outlined a migration approach using scalable architecture, hybrid cloud planning, load balancing, auto-scaling, CDN services, monitoring, and cloud-native performance dashboards.',
    ],
    outcomes: [
      'Created a six-page cloud strategy paper for CIS342 focused on organizational transformation through cloud adoption.',
      'Connected cloud technical choices to business outcomes such as long-term growth, innovation, operational resilience, and improved system reliability.',
      'Practiced explaining migration tradeoffs across economics, scalability, performance, security, compliance, and governance.',
    ],
    proof: [
      {
        title: 'Cloud Computing Strategy Paper',
        type: 'PDF artifact',
        description:
          'A six-page paper titled Cloud Computing Strategy for Organizational Transformation, written for CIS342 Cloud Computing and Virtualization.',
        href: '/projects/cloud-computing-strategy-for-organizational-transformation.pdf',
        linkLabel: 'Open PDF',
      },
      {
        title: 'Migration Planning',
        type: 'Strategy evidence',
        description:
          'The paper evaluates cloud adoption benefits and drawbacks while outlining a migration plan that considers economics, scalability, and performance.',
        href: '/projects/cloud-computing-strategy-for-organizational-transformation.pdf',
        linkLabel: 'Review strategy',
      },
      {
        title: 'Security and Compliance',
        type: 'Risk evidence',
        description:
          'The strategy discusses cloud security tooling, encryption, monitoring, compliance frameworks, and the need for governance during adoption.',
        href: '/projects/cloud-computing-strategy-for-organizational-transformation.pdf',
        linkLabel: 'View risk plan',
      },
    ],
  },
  {
    slug: 'database-management-projects',
    title: 'Database Design for Global Operations',
    description:
      'A database design and implementation proposal for GlobalTech Logistics, a mid-size international logistics company. The project covers data requirements, ER modeling, data flow diagrams, implementation planning, maintenance, governance, and database security.',
    meta: 'Database Design · ER Modeling · Data Governance · Security',
    challenge:
      'Design a relational database system that can support global logistics operations across customers, orders, shipments, warehouses, inventory, and employees while improving efficiency, real-time access, and data protection.',
    approach: [
      'Identified core data requirements and stakeholders for an international logistics company operating across North America, Europe, and Asia.',
      'Mapped entities, attributes, primary keys, and relationships for customers, orders, shipments, warehouses, inventory, and employees.',
      'Outlined implementation, maintenance, governance, and protection measures including cloud deployment, monitoring, RBAC, encryption, MFA, and database activity monitoring.',
    ],
    outcomes: [
      'Created a structured 20-slide database design presentation with business context, ER modeling, data flow planning, and implementation phases.',
      'Connected database structure to real operational needs such as shipment tracking, inventory visibility, warehouse management, and reporting.',
      'Practiced explaining database architecture, governance, maintenance, and security controls in a business-focused format.',
    ],
    proof: [
      {
        title: 'Database Design Presentation',
        type: 'PDF artifact',
        description:
          'A 20-page presentation titled Database Design and Implementation for Global Operations, covering GlobalTech Logistics requirements, ER models, DFDs, implementation, maintenance, and security.',
        href: '/projects/database-design-and-implementation.pdf',
        linkLabel: 'Open PDF',
      },
      {
        title: 'Original PowerPoint Deck',
        type: 'Source artifact',
        description:
          'The original PPTX deck is available as the editable source presentation behind the exported PDF artifact.',
        href: '/projects/database-design-and-implementation.pptx',
        linkLabel: 'Download PPTX',
      },
      {
        title: 'ER and Data Flow Planning',
        type: 'Design evidence',
        description:
          'The project identifies business entities, relationships, primary keys, data requirements, ER diagrams, and data flow diagrams to guide the relational database design.',
        href: '/projects/database-design-and-implementation.pdf',
        linkLabel: 'Review diagrams',
      },
      {
        title: 'Security and Governance',
        type: 'Process evidence',
        description:
          'The implementation plan includes database maintenance, data governance, role-based access control, encryption, multi-factor authentication, and activity monitoring.',
        href: '/projects/database-design-and-implementation.pdf',
        linkLabel: 'View security plan',
      },
    ],
  },
];

const strengths = [
  'Building full-stack applications with Next.js, React, TypeScript, and Supabase',
  'Working with SQL, relational data models, and database-enforced access controls',
  'Troubleshooting application workflows and writing browser and database tests',
  'Turning recurring responsibilities into organized, trackable task workflows',
  'Maintaining accurate documentation, incident reports, and operational records',
  'Coordinating team priorities and communicating clearly under time pressure',
];

const tools = [
  'Python',
  'C++',
  'Java',
  'SQL',
  'MySQL',
  'HTML5',
  'CSS3',
  'JavaScript',
  'React',
  'Next.js',
  'TypeScript',
  'Tailwind CSS',
  'Supabase',
  'PostgreSQL',
  'Git & GitHub',
  'Firebase',
  'Visual Studio Code',
  'Wireshark',
  'Microsoft Office',
  'Cloud Computing',
  'Network Security',
];

const socialLinks = [
  {
    label: 'GitHub',
    href: 'https://github.com/rumbxo',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/ahmand-edmonds-9b404b3bb/',
  },
];

const resumeHighlights = [
  {
    title: 'Direct Support Professional',
    details: [
      'Community Systems INC · Torrington, Connecticut',
      'October 2023 – Present',
      'Maintain accurate client documentation and incident reports to keep care plans and regulatory records up to date.',
      'Prepare reports, logs, daily progress notes, and service records that support audits, continuity of care, and team decisions.',
    ],
  },
  {
    title: 'Team Lead – Online Orders/Sales',
    details: [
      'Lowe’s · Southington, Connecticut',
      'March 2022 – October 2023',
      'Led daily online order fulfillment, assigned tasks, and adjusted priorities to meet time-sensitive performance targets.',
      'Coordinated team assignments, monitored progress, and provided operational guidance to keep orders moving under deadline pressure.',
    ],
  },
  {
    title: 'Rural Carrier Associate',
    details: [
      'United States Postal Service · Bristol, Connecticut',
      'January 2021 – March 2022',
      'Delivered mail, maintained accurate delivery logs, and recorded delivery attempts.',
      'Returned undeliverable mail to the post office for correct handling and disposition.',
    ],
  },
  {
    title: 'Career Focus',
    details: [
      'Application support, workflow automation, and practical uses of database and cloud technologies',
      'Seeking entry-level application support roles and AI or digital-transformation internships',
      'Based in Waterbury, Connecticut; open to remote, hybrid, and on-site opportunities',
    ],
  },
];

const targetRoles = [
  'Entry-level application support',
  'AI internships',
  'Digital-transformation internships',
];

const learningPlan = [
  {
    title: 'CompTIA A+',
    status: 'Planned',
    detail: 'Strengthen hardware, operating systems, troubleshooting, and IT support fundamentals.',
  },
  {
    title: 'CompTIA Network+',
    status: 'Planned',
    detail: 'Build deeper knowledge of networking, routing, protocols, security, and infrastructure support.',
  },
  {
    title: 'CompTIA Security+',
    status: 'Planned',
    detail: 'Continue developing security, risk management, access control, and incident response knowledge.',
  },
  {
    title: 'AWS Cloud Practitioner',
    status: 'Planned',
    detail: 'Validate cloud concepts, core AWS services, pricing, security, and shared responsibility.',
  },
];

const experienceItems = [
  {
    title: 'ShiftTrack Development',
    meta: '2026 – Present · Full-stack application',
    detail:
      'Built task assignment, status tracking, recurring workflows, and role-based access with Next.js and Supabase. Added browser and database tests to check workflow reliability and workspace separation.',
  },
  {
    title: 'Academic Technology Projects',
    meta: 'Post University · Computer Information Systems',
    detail:
      'Completed hands-on projects across web development, databases, cybersecurity, networking, cloud computing, systems analysis, and technical documentation.',
  },
  {
    title: 'Portfolio Development',
    meta: 'React · Firebase Hosting',
    detail:
      'Designed, built, and deployed this portfolio as a responsive single-page React application with project detail pages, resume access, and hosted production URLs.',
  },
  {
    title: 'Technical Communication',
    meta: 'Reports · Presentations · Documentation',
    detail:
      'Created technical reports and presentations that explain requirements, recommendations, risks, implementation plans, and project outcomes clearly.',
  },
];

function getProjectFromPath() {
  const match = window.location.pathname.match(/^\/projects\/([^/]+)\/?$/);

  if (!match) {
    return null;
  }

  return projects.find((project) => project.slug === match[1]) || null;
}

function isPlainClick(event) {
  return !event.defaultPrevented && event.button === 0 &&
    !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function LandingPage() {
  const [isNavHidden, setIsNavHidden] = useState(false);
  const [selectedProject, setSelectedProject] = useState(getProjectFromPath);

  useEffect(() => {
    if (!selectedProject) return;

    const originalTitle = document.title;
    const title = `${selectedProject.title} | Ahmand Edmonds`;
    const url = `https://ahmandedmonds.com/projects/${selectedProject.slug}`;
    const updates = [
      ['link[rel="canonical"]', 'href', url],
      ['meta[name="description"]', 'content', selectedProject.description],
      ['meta[property="og:title"]', 'content', title],
      ['meta[property="og:description"]', 'content', selectedProject.description],
      ['meta[property="og:url"]', 'content', url],
      ['meta[name="twitter:title"]', 'content', title],
      ['meta[name="twitter:description"]', 'content', selectedProject.description],
    ];
    const originals = updates.map(([selector, attribute, value]) => {
      const element = document.querySelector(selector);
      const original = element?.getAttribute(attribute);
      element?.setAttribute(attribute, value);
      return { element, attribute, original };
    });
    document.title = title;

    return () => {
      document.title = originalTitle;
      originals.forEach(({ element, attribute, original }) => {
        if (original == null) element?.removeAttribute(attribute);
        else element?.setAttribute(attribute, original);
      });
    };
  }, [selectedProject]);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY;

      setIsNavHidden(isScrollingDown && currentScrollY > 80);
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setSelectedProject(getProjectFromPath());
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const openProject = (event, project) => {
    if (!isPlainClick(event)) return;

    event.preventDefault();
    window.history.pushState({}, '', `/projects/${project.slug}`);
    setSelectedProject(project);
    window.requestAnimationFrame(() => {
      document.querySelector('.project-detail-hero h1')?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  };

  const navigateToSection = (event, sectionId) => {
    if (!selectedProject || !isPlainClick(event)) {
      return;
    }

    event.preventDefault();
    window.history.pushState({}, '', `/#${sectionId}`);
    setSelectedProject(null);

    window.requestAnimationFrame(() => {
      const section = document.getElementById(sectionId);
      section?.querySelector('h1, h2')?.focus({ preventScroll: true });
      section?.scrollIntoView({ behavior: 'auto' });
    });
  };

  return (
    <main className="landing-page">
      <nav
        className={`portfolio-nav${isNavHidden ? ' is-hidden' : ''}`}
        aria-label="Portfolio sections"
        onFocus={() => setIsNavHidden(false)}
      >
        <a
          className="brand"
          href="/#top"
          aria-label="Ahmand Edmonds home"
          onClick={(event) => navigateToSection(event, 'top')}
        >
          AE
        </a>

        <div className="nav-links">
          <a href="/#work" onClick={(event) => navigateToSection(event, 'work')}>
            Projects
          </a>
          <a href="/#about" onClick={(event) => navigateToSection(event, 'about')}>
            About
          </a>
          <a
            href="/#resume"
            onClick={(event) => navigateToSection(event, 'resume')}
          >
            Resume
          </a>
          <a
            href="/#contact"
            onClick={(event) => navigateToSection(event, 'contact')}
          >
            Contact
          </a>
        </div>
      </nav>

      {selectedProject ? (
        <>
          <ProjectDetail
            project={selectedProject}
            onBack={(event) => navigateToSection(event, 'work')}
          />
          <Footer onNavigate={navigateToSection} />
        </>
      ) : (
        <>

      <section id="top" className="hero">
        <div className="hero-copy">
          <p className="eyebrow">
            Application Support · Workflow Automation
          </p>

          <h1 tabIndex={-1}>
            Ahmand Edmonds
          </h1>

          <p className="intro">
            I’m a Computer Information Systems student at Post University,
            building practical applications that help teams organize work.
            Through ShiftTrack, I’m applying full-stack development, database
            design, and automated testing to everyday task workflows. I bring
            that technical foundation together with experience in documentation,
            frontline problem-solving, and team leadership.
          </p>

          <div className="hero-actions" aria-label="Primary portfolio actions">
            <a href="#work" className="btn btn-primary">
              Explore My Projects
            </a>

            <a href="#contact" className="btn btn-secondary">
              Contact Me
            </a>

            <a href="#resume" className="btn btn-secondary">
              View Resume
            </a>
          </div>
        </div>

        <aside className="hero-panel" aria-label="Professional snapshot">
          <div className="panel-header" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div className="panel-body">
            <div>
              <p className="panel-kicker">Current Focus</p>
              <h2>
                Application support, workflow automation, and reliable team tools.
              </h2>
            </div>

            <div className="signal-grid" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </aside>
      </section>

      <section id="work" className="section">
        <div className="section-heading">
          <p className="eyebrow">Selected Projects</p>
          <h2 tabIndex={-1}>Project work backed by real artifacts.</h2>
        </div>

        <div className="project-grid">
          {projects.map((project) => (
            <a
              className="project-card"
              href={`/projects/${project.slug}`}
              key={project.title}
              onClick={(event) => openProject(event, project)}
            >
              <p>{project.meta}</p>
              <h3>{project.title}</h3>
              <span>{project.description}</span>
              <strong>View project proof</strong>
            </a>
          ))}
        </div>
      </section>

      <section id="about" className="section about-section">
        <div className="section-heading">
          <p className="eyebrow">About Me</p>
          <h2 tabIndex={-1}>Connecting technical skills with everyday operations.</h2>
        </div>

        <div className="about-content">
          <div className="about-summary">
            <p>
              I’m based in Waterbury, Connecticut, and pursuing a Bachelor of
              Science in Computer Information Systems at Post University, with
              graduation expected in December 2026. My coursework spans data
              analytics, cloud computing, databases, data structures, and
              algorithms. I’ve earned Dean’s List recognition twice.
            </p>
            <p>
              My work as a Direct Support Professional has strengthened my
              attention to documentation, continuity of care, and clear team
              communication. Previously, leading online order fulfillment at
              Lowe’s taught me to coordinate assignments and adjust priorities
              under pressure. I bring those habits to projects like ShiftTrack
              and want to apply them in application support, automation, and
              digital transformation.
            </p>
          </div>

          <ul className="strength-list">
            {strengths.map((strength) => (
              <li key={strength}>{strength}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section looking-section" aria-labelledby="looking-title">
        <div className="section-heading">
          <p className="eyebrow">What I’m Looking For</p>
          <h2 id="looking-title">Application support and digital-transformation opportunities.</h2>
        </div>

        <div className="role-list">
          {targetRoles.map((role) => (
            <span key={role}>{role}</span>
          ))}
        </div>
      </section>

      <section
        className="section tools-section"
        aria-labelledby="education-title"
      >
        <div>
          <p className="eyebrow">Education</p>
          <h2 id="education-title">Post University</h2>
        </div>

        <div className="about-content">
          <p>
            <strong>Bachelor of Science in Computer Information Systems</strong>
            <br />
            Expected graduation: December 2026
            <br />
            Waterbury, Connecticut
            <br />
            Dean’s List recognition twice
          </p>
        </div>
      </section>

      <section className="section experience-section" aria-labelledby="experience-title">
        <div className="section-heading">
          <p className="eyebrow">Experience</p>
          <h2 id="experience-title">Application development and technical communication.</h2>
        </div>

        <div className="experience-list">
          {experienceItems.map((item) => (
            <article className="experience-item" key={item.title}>
              <div>
                <p>{item.meta}</p>
                <h3>{item.title}</h3>
              </div>
              <span>{item.detail}</span>
            </article>
          ))}
        </div>
      </section>

      <section
        className="section tools-section"
        aria-labelledby="tools-title"
      >
        <div>
          <p className="eyebrow">Technical Toolkit</p>
          <h2 id="tools-title">Technologies behind my work.</h2>
        </div>

        <div className="tool-list">
          {tools.map((tool) => (
            <span key={tool}>{tool}</span>
          ))}
        </div>
      </section>

      <section className="section certifications-section" aria-labelledby="certifications-title">
        <div className="section-heading">
          <p className="eyebrow">Learning Roadmap</p>
          <h2 id="certifications-title">Certifications I’m preparing to pursue.</h2>
        </div>

        <div className="certification-grid">
          {learningPlan.map((item) => (
            <article className="certification-card" key={item.title}>
              <div>
                <span>{item.status}</span>
                <h3>{item.title}</h3>
              </div>
              <p>{item.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="resume" className="section resume-section">
        <div className="section-heading">
          <p className="eyebrow">Resume</p>
          <h2 tabIndex={-1}>Work history, leadership, and career focus.</h2>
        </div>

        <div className="resume-grid">
          {resumeHighlights.map((item) => (
            <article className="resume-card" key={item.title}>
              <h3>{item.title}</h3>
              <ul>
                {item.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="resume-callout">
          <p>
            Open or download the PDF version of my resume for a concise look at
            my work experience, education, technical skills, and project
            experience.
          </p>
          <a
            className="btn btn-primary"
            href="/Ahmand_Edmonds_Resume.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Open Resume PDF
          </a>
        </div>
      </section>

      <section id="contact" className="section contact-section">
        <div>
          <p className="eyebrow">Contact</p>
          <h2 tabIndex={-1}>Let’s connect.</h2>

          <p>
            I’m seeking entry-level application support roles and AI or
            digital-transformation internships where I can help teams
            troubleshoot issues, improve workflows, and automate routine work.
            Based in Waterbury, Connecticut, I’m open to remote, hybrid, and
            on-site opportunities.
          </p>
        </div>

        <div className="contact-actions">
          <a
            className="btn btn-primary"
            href="mailto:ahmandedmonds@gmail.com"
            aria-label="Email Ahmand Edmonds"
          >
            Email Me
          </a>

          {socialLinks.map((link) => (
            <a
              className="btn btn-secondary"
              href={link.href}
              key={link.label}
              target="_blank"
              rel="noreferrer"
            >
              {link.label}
            </a>
          ))}
        </div>
      </section>

          <Footer onNavigate={navigateToSection} />
        </>
      )}
    </main>
  );
}

function ProjectDetail({ project, onBack }) {
  return (
    <article className="project-detail">
      <a className="back-link" href="/#work" onClick={onBack}>
        Back to projects
      </a>

      <header className="project-detail-hero">
        <p className="eyebrow">{project.meta}</p>
        <h1 tabIndex={-1}>{project.title}</h1>
        <p>{project.description}</p>
      </header>

      <div className="project-detail-grid">
        <section className="detail-panel">
          <p className="eyebrow">Challenge</p>
          <h2>What the project needed to solve</h2>
          <p>{project.challenge}</p>
        </section>

        <section className="detail-panel">
          <p className="eyebrow">Approach</p>
          <h2>How I worked through it</h2>
          <ul className="detail-list">
            {project.approach.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="detail-panel detail-panel-wide">
          <p className="eyebrow">Outcomes</p>
          <h2>What I took away</h2>
          <ul className="detail-list">
            {project.outcomes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        {project.proof && (
          <section className="detail-panel detail-panel-wide proof-panel">
            <p className="eyebrow">Proof / Artifacts</p>
            <h2>Evidence behind the project</h2>
            <div className="proof-grid">
              {project.proof.map((item) => (
                <article className="proof-card" key={item.title}>
                  <p>{item.type}</p>
                  <h3>{item.title}</h3>
                  <span>{item.description}</span>
                  {item.href && (
                    <a href={item.href} target="_blank" rel="noreferrer">
                      {item.linkLabel || 'View artifact'}
                    </a>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}

function Footer({ onNavigate }) {
  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <div>
          <a
            className="footer-brand"
            href="/#top"
            aria-label="Back to top"
            onClick={(event) => onNavigate(event, 'top')}
          >
            Ahmand Edmonds
          </a>
          <p>
            Computer Information Systems student focused on application support,
            workflow automation, and practical software development.
          </p>
        </div>

        <div className="footer-links" aria-label="Footer links">
          <a href="/#work" onClick={(event) => onNavigate(event, 'work')}>
            Projects
          </a>
          <a href="/#about" onClick={(event) => onNavigate(event, 'about')}>
            About
          </a>
          <a href="/#resume" onClick={(event) => onNavigate(event, 'resume')}>
            Resume
          </a>
          <a href="/#contact" onClick={(event) => onNavigate(event, 'contact')}>
            Contact
          </a>
          {socialLinks.map((link) => (
            <a
              href={link.href}
              key={link.label}
              target="_blank"
              rel="noreferrer"
            >
              {link.label}
            </a>
          ))}
          <a href="mailto:ahmandedmonds@gmail.com">Email</a>
        </div>
      </div>

      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Ahmand Edmonds</span>
        <span>Built with React and Firebase Hosting</span>
      </div>
    </footer>
  );
}

export default LandingPage;
